import { apiClient } from "@/lib/api-client";
import { IApiResponse } from "@/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface INotification {
  id: string;
  userId: string;
  tenantId: string;
  type: string;
  priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
  title: string;
  message: string;
  linkUrl?: string;
  isRead: boolean;
  readAt?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface INotificationsResponse {
  notifications: INotification[];
  summary: {
    total: number;
    unread: number;
  };
}

interface INotificationsQueryParams {
  unreadOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Hook to fetch notifications for the current user
 */
export function useNotifications(params?: INotificationsQueryParams) {
  return useQuery({
    queryKey: ["notifications", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.unreadOnly) {
        searchParams.set("unreadOnly", "true");
      }
      if (params?.page) {
        searchParams.set("page", params.page.toString());
      }
      if (params?.pageSize) {
        searchParams.set("pageSize", params.pageSize.toString());
      }

      // apiClient is configured with baseURL (usually "/api"), so paths here should not include "/api"
      const url = `/notifications${
        searchParams.toString() ? `?${searchParams.toString()}` : ""
      }`;
      const response = await apiClient.get<
        IApiResponse<INotificationsResponse>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.patch<
        IApiResponse<{ id: string; isRead: boolean; readAt?: string }>
      >(`/notifications/${notificationId}/read`, {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate notifications to refetch
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<
        IApiResponse<{ markedRead: number }>
      >("/notifications/mark-all-read", {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate notifications to refetch
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
